import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { SharedModule } from '@/app/shared/shared.module';
import { PrecipitationAnalysisDataViewModel, SatelliteViewModel } from '@/app/proxy/proxies';
import { EChartsOption } from 'echarts';
import { PlantationService } from '@/app/core/services/plantation.service';
import { NoAnalysisComponent } from '../no-analysis/no-analysis.component';
import { CropHealthService } from '../../services/crop-health.service';
import { DateTime } from 'luxon';
import { LuxonFormatPipe } from '@/app/shared/pipes/luxon-format.pipe';

@Component({
  selector: 'app-rainfall-summary',
  imports: [SharedModule, NoAnalysisComponent],
  templateUrl: './rainfall-summary.component.html',
  styleUrl: './rainfall-summary.component.less',
  providers: [LuxonFormatPipe]
})
export class RainfallSummaryComponent implements OnInit, OnChanges {
  private luxonFormatPipe = inject(LuxonFormatPipe);
  @Input() loading = false;
  @Input() data: PrecipitationAnalysisDataViewModel[] = [];
  @Input() selectedDay: SatelliteViewModel;
  lastValue: PrecipitationAnalysisDataViewModel;

  chartOption!: EChartsOption;

  constructor(public plantationService: PlantationService,
    public cropHealthService: CropHealthService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['data']) {
      this.makeChart();
    }
  }

  ngOnInit(): void { }

  makeChart() {
    if (!(this.data?.length > 0)) {
      this.chartOption = null;
      return;
    }

    this.lastValue = this.data[this.data.length - 1];
    const min = +Math.min(...this.data.map((item) => item.precipitation)).toFixed(2);
    const max = +Math.max(...this.data.map((item) => item.precipitation)).toFixed(2);

    const startDate = this.data[0].date.toJSDate();
    const endDate = this.data[this.data.length - 1].date.toJSDate();

    // Generate tick positions (1st day of each month between start and end)
    const ticks: Date[] = [];
    const current = new Date(startDate);
    current.setDate(1);

    while (current <= endDate) {
      ticks.push(new Date(current));
      current.setMonth(current.getMonth() + 1);
    }

    this.chartOption = {
      title: {
        show: false,
      },
      grid: {
        left: '50px',
        right: '50px',
        top: '10px',
        bottom: '50px',
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#f0f0f0',
            borderColor: '#bbb',
            borderWidth: 1,
            color: '#000',
          },
        },
        formatter: (params: any) => {
          const index = params[0].dataIndex;
          const date = this.luxonFormatPipe.transform(
            this.data[index].date,
            'yyyy-MM-dd'
          );

          return `
            <b>${date}</b><br/>
            ${this.data[index].precipitation?.toFixed(2)} mm
          `;
        },
      },
      xAxis: {
        type: 'time',
        min: startDate,
        max: endDate,
        axisLabel: {
          formatter: (value: number) => {
            const date = new Date(value);
            const luxonDate = DateTime.fromJSDate(date);
            return this.luxonFormatPipe.transform(
              luxonDate,
              'LLL',
            );
          },
        },
        axisTick: {
          show: true,
        },
        splitLine: { show: false },
        splitNumber: ticks.length,
      },
      yAxis: {
        axisLine: {
          show: false,
        },
        axisLabel: {
          color: '#444',
        },
        splitLine: {
          lineStyle: {
            color: '#e0e0e0',
            type: 'dashed',
          },
        },
        splitNumber: 4,
        min,
        max,
        interval: 100,
      },
      series: [
        {
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: this.data.map((item) => [item.date.toJSDate(), item.precipitation]),
          lineStyle: {
            width: 5,
            color: '#092ABE',
          },
        },
      ],
    };
  }
}
